'use client';

import { useEffect, useRef, useState } from 'react';

export const experimental_ppr = true;

export default function Layout({
  children,
}: {
  children: React.ReactNode;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [colorMode, setColorMode] = useState('0,0,0'); // Default to dark mode
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark mode
  const [mousePosition, setMousePosition] = useState({ x: -1, y: -1 }); // Default value
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    // Check for color scheme preference
    if (window.matchMedia) {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setColorMode(isDark ? '0,0,0' : '255,255,255');
      setIsDarkMode(isDark);

      // Listen for changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setColorMode(e.matches ? '0,0,0' : '255,255,255');
        setIsDarkMode(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (titleRef.current) {
        const rect = titleRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMousePosition({ x, y });
        setInitialLoad(false);
      }
    };

    // Set a default good-looking position when the component mounts
    if (initialLoad && titleRef.current) {
      const rect = titleRef.current.getBoundingClientRect();
      // Default position at 1/3 from the right and middle of height
      setMousePosition({
        x: rect.width * 0.7,
        y: rect.height * 0.5,
      });
    }

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [initialLoad]);

  // Calculate lighting effect based on mouse position
  const calculateLighting = () => {
    if (!titleRef.current) return {};

    const rect = titleRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Normalize mouse position relative to center with increased impact
    const normX = (mousePosition.x - centerX) / centerX; // Full impact (removed 0.5 multiplier)
    const normY = (mousePosition.y - centerY) / centerY; // Full impact (removed 0.5 multiplier)

    // Calculate highlight position with increased range
    const highlightX = 50 + normX * 25; // Increased from 12 to 25
    const highlightY = 50 + normY * 25; // Increased from 12 to 25

    // Calculate shadow direction (opposite of light) with increased range
    const shadowX = Math.max(-5, Math.min(5, -normX * 5)); // Increased limit to -5px to 5px
    const shadowY = Math.max(-5, Math.min(5, -normY * 5)); // Increased limit to -5px to 5px

    return {
      background: `linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)`,
      textShadow: `${shadowX}px ${shadowY}px 20px rgba(0,0,0,0.2), 0 0 25px rgba(255,255,255,0.2)`, // Increased blur radius for more blurriness
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
      backgroundImage: isDarkMode
        ? `
          linear-gradient(
            135deg,
            rgba(255,255,255,0.9) ${highlightX - 30}%,
            rgba(255,255,255,0.5) ${highlightX}%,
            rgba(120,180,255,0.8) ${highlightX + 30}%
          )
        `
        : `
          linear-gradient(
            135deg,
            rgba(0,0,0,0.9) ${highlightX - 30}%,
            rgba(0,30,100,0.8) ${highlightX}%,
            rgba(50,120,230,0.9) ${highlightX + 30}%
          )
        `,
      filter: isDarkMode
        ? 'drop-shadow(0 0 12px rgba(100,180,255,0.35))'
        : 'drop-shadow(0 0 12px rgba(0,50,150,0.35))',
    };
  };

  return (
    <div
      className="flex h-dvh w-screen items-center justify-center"
      style={{
        position: 'relative',
        background:
          'url("/images/image_bg bg.png") no-repeat center center fixed',
        backgroundSize: 'cover',
        overflow: 'hidden',
      }}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
        poster="/images/image_bg bg.png"
      >
        <source
          src="/images/video_bg.mp4"
          type="video/mp4"
        />
        {/* Image will be used as fallback through the poster attribute */}
      </video>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at center 80%, rgba(var(--mask-color),0.05) 0%, rgba(var(--mask-color),0.1) 10%, rgba(var(--mask-color),0.15) 30%, rgba(var(--mask-color),0.25) 35%, rgba(var(--mask-color),0.4) 45%, rgba(var(--mask-color),0.5) 70%, rgba(var(--mask-color),0.6) 100%)',
          ['--mask-color' as string]: colorMode,
        }}
        className="z-10"
      />
      <div className="flex flex-col items-center justify-center gap-10 z-20">
        <h1
          ref={titleRef}
          className="text-4xl font-medium uppercase"
          style={{
            ...calculateLighting(),
            padding: '0.5em',
            letterSpacing: '2px',
          }}
        >
          INDUSTRY INTELLIGENCE
        </h1>
        <div
          className="w-full max-w-md overflow-hidden rounded-2xl shadow-md flex flex-col gap-8 py-8 "
          style={{
            backgroundColor: isDarkMode
              ? 'rgba(39, 39, 42, 0.6)'
              : 'rgba(240, 240, 245, 0.6)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            minHeight: '380px',
            border: isDarkMode
              ? '1px solid rgba(82, 82, 91, 0.6)'
              : '1px solid rgba(200, 200, 210, 0.6)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
